import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from "react";
import api from "../../../utils/api";
import ImageUploadPreview from "../../../components/restaurant/ImageUploadPreview";
import { FaTrashAlt } from "react-icons/fa";
import { empty } from "../../../helper/generalHelper";
import CategoryDropdown from "../../../components/common/CategoryDropdown";

const generateSKU = () => `SKU-${Math.floor(100000 + Math.random() * 900000)}`;

const supportedLanguages = ["en", "hi"];

const MenuFormEditStaff = forwardRef(
  ({ categories, inventory, onDelete, initialData = {} }, ref) => {


    const [menu, setMenu] = useState({
      categoryId: "",
      sku: "",
      mrp: "",
      price: "",
      quantity: "",
      hasTax: 0,
      tax: "",
      inventory_manage: 0,
      ingredients: [],
      images: [],
      uploads:[],
      activeLang: "en",
      has_variation: 0,
      translations: supportedLanguages.reduce((acc, lang) => {
        acc[lang] = { title: "", shortDescription: "", description: "" };
        return acc;
      }, {}),
    });

    const [uploads, setUploads] = useState([]);
 
    // === ✅ Populate form from initialData (edit mode) ===
    useEffect(() => {
      if (!initialData || !initialData.id) return;

      const formattedData = {
        categoryId: initialData.category_id || "",
        sku: initialData.sku || "",
        mrp: initialData.mrp !== undefined && initialData.mrp !== null ? initialData.mrp : "",
        price: initialData.price !== undefined && initialData.price !== null ? initialData.price : "",
        quantity: initialData.quantity !== undefined && initialData.quantity !== null ? initialData.quantity : "",
        hasTax: (Number(initialData.has_tax) === 1 && parseFloat(initialData.tax) > 0) ? 1 : 0,
        tax: initialData.tax || "",
        inventory_manage: initialData.inventory_manage || 0,
        ingredients: initialData.ingredients || [],
        images: initialData?.images || [],
        uploads: initialData?.uploads || [],
        activeLang: "en",
        has_variation: initialData.has_variation,
        translations: {
          ...supportedLanguages.reduce((acc, lang) => {
            acc[lang] = {
              title: initialData.translations?.[lang]?.title || "",
              shortDescription:
                initialData.translations?.[lang]?.shortDescription || "",
              description: initialData.translations?.[lang]?.description || "",
            };
            return acc;
          }, {}),
        },
      };

      setMenu((prev) => ({
        ...prev,
        ...formattedData,
      }));

      setUploads(initialData.menuImages || []);

    }, [initialData]);

    // --- FIELD UPDATERS ---
    const updateField = (field, value) => {
      setMenu((prev) => ({ ...prev, [field]: value }));
    };

    const updateTranslation = (lang, field, value) => {
      setMenu((prev) => ({
        ...prev,
        translations: {
          ...prev.translations,
          [lang]: {
            ...prev.translations[lang],
            [field]: value,
          },
        },
      }));
    };

    const toggleField = (field) => {
      setMenu((prev) => ({
        ...prev,
        [field]: prev[field] === 1 ? 0 : 1,
      }));
    };

    const addIngredient = () => {
      setMenu((prev) => ({
        ...prev,
        ingredients: [
          ...prev.ingredients,
          { id: null, inventoryId: "", measurement: "", reduceStock: "" },
        ],
      }));
    };

    const updateIngredient = (index, field, value) => {
      const updated = [...menu.ingredients];
      updated[index][field] = value;
      setMenu((prev) => ({ ...prev, ingredients: updated }));
    };

    const removeIngredient = (index) => {
      setMenu((prev) => ({
        ...prev,
        ingredients: prev.ingredients.filter((_, i) => i !== index),
      }));
    };

    const handleFileUpload = async (e) => {
      const files = Array.from(e.target.files);
      const formData = new FormData();
      files.forEach((file) => formData.append("images[]", file));

      try {
        const res = await api.post(
          "restaurant-staff/restaurant/menu/upload-images",
          formData
        );
        const uploaded = res.data.images;
        setUploads((prev) => [...prev, ...uploaded]);
        setMenu((prev) => ({
          ...prev,
          images: [...prev.images, ...uploaded],
        }));
      } catch (err) {
        console.error("Upload error:", err);
      }
    };

    // === 🧠 Expose data to parent using ref ===
    useImperativeHandle(ref, () => ({
      getSanitizedData: () => {
        return {
          id: initialData?.id || undefined, // ✅ Include `id` if editing
          ...menu,
          sku: menu.sku || generateSKU(),
          categoryId: menu.categoryId !== "" ? Number(menu.categoryId) : null,
          mrp: menu.mrp !== "" ? Number(menu.mrp) : 0,
          price: menu.price !== "" ? Number(menu.price) : 0,
          quantity: menu.quantity !== "" ? Number(menu.quantity) : 0,
          hasTax: Number(menu.hasTax),
          tax: menu.hasTax ? (menu.tax !== "" ? Number(menu.tax) : 0) : 0,
          inventory_manage: Number(menu.inventory_manage),
          ingredients:
            menu.inventory_manage === 1
              ? menu.ingredients
                  .filter((ing) => ing.inventoryId && ing.inventoryId !== "")
                  .map((ing) => ({
                    id: ing.id || null,
                    inventoryId: ing.inventoryId,
                    measurement: empty(ing.measurement) ? null : ing.measurement,
                    reduceStock:
                      ing.reduceStock !== "" ? Number(ing.reduceStock) : 0,
                  }))
              : [],
          translations: menu.translations,
          images: menu.images,
        };
      },
      getUploads: () => uploads,
    }));

    // ✅ All rendering code remains the same below (unchanged)
    // (Cut for brevity here, you already have it)

    return (
      <div className="max-w-5xl mx-auto bg-white shadow-md rounded-lg p-6 sm:p-8 mb-10 relative">
        {onDelete && (
          <button
            onClick={onDelete}
            className="absolute top-4 right-4 text-red-600 hover:text-red-800 text-xl"
            title="Remove this Menu"
          >
            &times;
          </button>
        )}

        {/* Basic Info */}
        <div className="mb-8">
          {/* <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Basic Info
          </h3> */}

          {/* Translations */}
          <div>
            <div className="flex space-x-2 mb-3">
              {supportedLanguages.map((lang) => (
                <button
                  key={lang}
                  type="button"
                  onClick={() => updateField("activeLang", lang)}
                  className={`px-3 py-1.5 text-sm rounded-md font-medium border ${
                    menu.activeLang === lang
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {lang.toUpperCase()}
                </button>
              ))}
            </div>

            <div className="space-y-4">
               {[{ label: "Item Name", field: "title" },{ label:"shortDescription", field: "shortDescription" },{ label:"description", field: "description" }].map(({field, label }) => {
            const Component = field === "description" ? "textarea" : "input";
                 label =
                  label === "shortDescription"
                    ? "Short Description"
                    : label.charAt(0).toUpperCase() + label.slice(1);
                return (
                  <div key={field}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {label} ({menu.activeLang.toUpperCase()})
                    </label>
                    <Component
                      rows={field === "description" ? 4 : undefined}
                      value={menu.translations[menu.activeLang][field]}
                      onChange={(e) =>
                        updateTranslation(
                          menu.activeLang,
                          field,
                          e.target.value
                        )
                      }
                      placeholder={`${label} (${menu.activeLang})`}
                      className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* SKU */}
            <div className="flex flex-col justify-center h-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                SKU
              </label>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <input
                  type="text"
                  value={menu.sku}
                  onChange={(e) => updateField("sku", e.target.value)}
                  className="flex-grow border px-3 py-2 rounded-md text-sm"
                />
                <button
                  type="button"
                  onClick={() => updateField("sku", generateSKU())}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-md"
                >
                  Generate
                </button>
              </div>
            </div>

            {/* Category */}
            <div className="flex flex-col justify-center h-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <CategoryDropdown
                value={menu.categoryId}
                onChange={(val) => updateField("categoryId", val)}
                categories={categories}
                placeholder="Select Category"
              />
            </div>
          </div>
        </div>

        {/* Pricing & Inventory */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Pricing & Inventory
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {["mrp", "price", "quantity"].map((field) => (
              <div key={field}>
                <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">
                  {field}
                </label>
                <input
                  type="number" step="any"
                  min="0"
                  value={menu[field]}
                  onChange={(e) => updateField(field, e.target.value)}
                  onWheel={(e) => e.target.blur()}
                  className="w-full border px-3 py-2 rounded-md text-sm"
                />
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Has Tax Toggle */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700 font-medium">
                Include Tax
              </span>
              <button
                type="button"
                onClick={() => toggleField("hasTax")}
                className={`relative inline-flex items-center h-6 w-12 rounded-full transition-colors ${
                  menu.hasTax ? "bg-green-500" : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block w-5 h-5 transform bg-white rounded-full shadow transition-transform ${
                    menu.hasTax ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            {/* Inventory Manage Toggle */}
            <div className="flex items-center justify-end gap-2">
              <span className="text-sm text-gray-700 font-medium">
                Inventory Manage
              </span>
              <button
                type="button"
                onClick={() => toggleField("inventory_manage")}
                className={`relative inline-flex items-center h-6 w-12 rounded-full transition-colors ${
                  menu.inventory_manage ? "bg-green-500" : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block w-5 h-5 transform bg-white rounded-full shadow transition-transform ${
                    menu.inventory_manage ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          </div>

          {menu.hasTax === 1 && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tax %
              </label>
              <input
                type="number" step="any"
                min="0"
                value={menu.tax}
                onChange={(e) => updateField("tax", e.target.value)}
                className=" border px-3 py-2 rounded-md text-sm"
              />
            </div>
          )}
        </div>

        {/* Ingredients */}
        {menu.inventory_manage === 1 && (
          <div className="mb-6">
            <h3 className="text-base font-semibold text-gray-800 mb-3">
              Ingredients
            </h3>

            {menu.ingredients.length === 0 && (
              <p className="text-sm text-gray-500 mb-4">
                No ingredients added yet.
              </p>
            )}

            {/* Table Headers */}
            {menu.ingredients.length > 0 && (
              <div className="hidden sm:grid grid-cols-4 gap-4 px-2 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded-t-md">
                <div>Ingredient</div>
                <div>Measurement</div>
                <div>Reduce Stock</div>
                <div>Action</div>
              </div>
            )}

            {/* Table Rows */}
            {menu.ingredients.map((ing, i) => (
              <div
                key={i}
                className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-center py-2 border-b"
              >
                {/* Ingredient Select */}
                <select
                  value={ing.inventoryId || ""}
                  onChange={(e) => updateIngredient(i, "inventoryId", e.target.value)}
                  className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Ingredient</option>
                  {inventory.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.itemName} ({item.measurement})
                    </option>
                  ))}
                </select>

                {/* Measurement Select */}
                <select
                  value={ing.measurement}
                  onChange={(e) =>
                    updateIngredient(i, "measurement", e.target.value)
                  }
                  className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Measurement</option>
                  <option value="kg">kg</option>
                  <option value="g">g</option>
                  <option value="l">l</option>
                  <option value="ml">ml</option>
                  <option value="piece">piece</option>
                </select>

                {/* Reduce Stock Input */}
                <input
                  type="number"
                  min="0"
                  placeholder="Reduce Stock"
                  value={ing.reduceStock}
                  onChange={(e) =>
                    updateIngredient(i, "reduceStock", e.target.value)
                  }
                  className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />

                {/* Delete Button */}
                <button
                  type="button"
                  onClick={() => removeIngredient(i)}
                  className="text-red-600 hover:text-red-800 flex justify-start sm:justify-center"
                  title="Remove Ingredient"
                >
                  <FaTrashAlt size={18} />
                </button>
              </div>
            ))}

            {/* Add Ingredient Button */}
            <button
              type="button"
              onClick={addIngredient}
              className="mt-4 bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded-md"
            >
              + Add Ingredient
            </button>
          </div>
        )}

        {/* Image Upload */}
        <div className="mb-6">
          <ImageUploadPreview
            initialImages={menu?.uploads}
            onChange={(imgs) => updateField("images", imgs)}
            onUpload={handleFileUpload}
          />
        </div>
      </div>
    );
  }
);

export default MenuFormEditStaff;
